/**
 * MobileDashboardFooter — Conteúdo de engajamento para preencher
 * o espaço vazio do dashboard mobile do professor.
 * Inclui: Dica do Dia, Pilares do Programa, Frase Motivacional.
 */
import { useState, useMemo } from 'react';
import {
  Lightbulb, Shield, MessageCircle, BarChart3, Brain, Heart, Wifi,
  ChevronRight, Sparkles, BookOpen, GraduationCap,
} from 'lucide-react';

// ── Dicas rotativas (pedagógicas e de uso do app) ──
const DICAS = [
  { icon: Lightbulb, title: 'Chamada inteligente', text: 'Ao fazer a chamada, voce tambem pode registrar observacoes sobre o comportamento dos alunos diretamente na tela.' },
  { icon: BookOpen, title: 'Observacoes privadas', text: 'Observacoes marcadas como privadas ficam visiveis apenas para voce. Para avisar a familia, use Meus Alunos → Avisar responsavel.' },
  { icon: GraduationCap, title: 'Avaliacoes rapidas', text: 'Use o botao de lancamento de notas para registrar notas de todos os alunos de uma turma em poucos toques.' },
  { icon: BarChart3, title: 'Relatorios de frequencia', text: 'Acesse os relatorios para identificar alunos com baixa frequencia e agir preventivamente antes que se torne critico.' },
  { icon: MessageCircle, title: 'Avisar responsavel', text: 'Em Meus Alunos, use o icone de mensagem para enviar um aviso padronizado a um responsavel especifico (comportamento, elogio, etc.).' },
  { icon: Shield, title: 'Seguranca escolar', text: 'Toda movimentacao dentro do app e registrada. Isso garante rastreabilidade e seguranca para voce e para a escola.' },
  { icon: Heart, title: 'Bem-estar do aluno', text: 'Observe padroes de comportamento e ausencia. O sistema pode alertar automaticamente quando um aluno precisa de atencao especial.' },
];

// ── Pilares do programa (dados ricos) ──
import {
  Lock, Eye, FileCheck, Bell, Users as UsersIcon, Smartphone,
  PieChart, TrendingUp, Cpu, Zap, HandHeart, Globe,
  CheckCircle2,
} from 'lucide-react';

const PILARES = [
  {
    icon: Shield, label: 'Seguranca Escolar', color: '#2563eb', bg: 'rgba(37,99,235,0.08)',
    gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
    desc: 'Controle total de acesso, auditoria e rastreabilidade em tempo real para proteger alunos, professores e a comunidade.',
    features: [
      { icon: Lock, text: 'Login seguro com biometria e sessao unica' },
      { icon: Eye, text: 'Auditoria completa de todas as acoes no sistema' },
      { icon: FileCheck, text: 'Registro digital de presenca e movimentacao' },
    ],
  },
  {
    icon: MessageCircle, label: 'Comunicacao Digital', color: '#059669', bg: 'rgba(5,150,105,0.08)',
    gradient: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
    desc: 'Ponte digital que conecta escola, professores e familias com comunicacao instantanea e rastreavel.',
    features: [
      { icon: Bell, text: 'Notificacoes em tempo real para responsaveis' },
      { icon: MessageCircle, text: 'Comunicados oficiais com confirmacao de leitura' },
      { icon: UsersIcon, text: 'Canal direto entre professor e familia do aluno' },
    ],
  },
  {
    icon: BarChart3, label: 'Gestao Integrada', color: '#d97706', bg: 'rgba(217,119,6,0.08)',
    gradient: 'linear-gradient(135deg, #92400e 0%, #f59e0b 100%)',
    desc: 'Dados centralizados para decisoes pedagogicas e administrativas embasadas em evidencias.',
    features: [
      { icon: PieChart, text: 'Dashboards em tempo real por turma e escola' },
      { icon: TrendingUp, text: 'Indicadores de frequencia, notas e risco' },
      { icon: FileCheck, text: 'Relatorios automaticos para secretarias' },
    ],
  },
  {
    icon: Brain, label: 'Inteligencia com IA', color: '#7c3aed', bg: 'rgba(124,58,237,0.08)',
    gradient: 'linear-gradient(135deg, #5b21b6 0%, #8b5cf6 100%)',
    desc: 'Inteligencia artificial aplicada para identificar padroes, antecipar riscos e personalizar o ensino.',
    features: [
      { icon: Cpu, text: 'Deteccao automatica de alunos em risco' },
      { icon: Zap, text: 'Alertas preditivos baseados em comportamento' },
      { icon: Brain, text: 'Sugestoes pedagogicas personalizadas' },
    ],
  },
  {
    icon: Heart, label: 'Apoio Social', color: '#dc2626', bg: 'rgba(220,38,38,0.08)',
    gradient: 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)',
    desc: 'Suporte ao bem-estar emocional e social de alunos e familias em situacao de vulnerabilidade.',
    features: [
      { icon: HandHeart, text: 'Observacoes sigilosas sobre bem-estar do aluno' },
      { icon: Heart, text: 'Acompanhamento emocional com privacidade' },
      { icon: UsersIcon, text: 'Rede de apoio entre escola e comunidade' },
    ],
  },
  {
    icon: Wifi, label: 'Infraestrutura', color: '#0891b2', bg: 'rgba(8,145,178,0.08)',
    gradient: 'linear-gradient(135deg, #155e75 0%, #06b6d4 100%)',
    desc: 'Tecnologia acessivel que funciona mesmo em ambientes com internet limitada ou instavel.',
    features: [
      { icon: Smartphone, text: 'App mobile otimizado para celulares simples' },
      { icon: Globe, text: 'Funcionamento offline com sincronizacao' },
      { icon: Wifi, text: 'Infraestrutura escalavel para milhares de usuarios' },
    ],
  },
];

// ── Frases motivacionais ──
const FRASES = [
  { texto: 'Educar e semear com sabedoria e colher com paciencia.', autor: 'Augusto Cury' },
  { texto: 'A educacao e a arma mais poderosa que voce pode usar para mudar o mundo.', autor: 'Nelson Mandela' },
  { texto: 'O professor e aquele que faz duas ideias crescerem onde antes so crescia uma.', autor: 'Elbert Hubbard' },
  { texto: 'Ensinar nao e transferir conhecimento, mas criar possibilidades para sua producao.', autor: 'Paulo Freire' },
  { texto: 'A tarefa essencial do professor e despertar a alegria de trabalhar e de conhecer.', autor: 'Albert Einstein' },
  { texto: 'Voce nao pode ensinar nada a um homem; so pode ajuda-lo a encontrar dentro de si.', autor: 'Galileu Galilei' },
  { texto: 'A mente que se abre a uma nova ideia jamais volta ao seu tamanho original.', autor: 'Albert Einstein' },
];

export function MobileDashboardFooter() {
  // Dica do dia baseada na data (muda a cada dia)
  const dicaIdx = useMemo(() => {
    const d = new Date();
    return (d.getFullYear() * 366 + d.getMonth() * 31 + d.getDate()) % DICAS.length;
  }, []);
  const dica = DICAS[dicaIdx];
  const DicaIcon = dica.icon;

  // Frase do dia
  const fraseIdx = useMemo(() => {
    const d = new Date();
    return (d.getDate() + d.getMonth()) % FRASES.length;
  }, []);
  const frase = FRASES[fraseIdx];

  // Pilares expandidos
  const [pilarExpandido, setPilarExpandido] = useState<number | null>(null);

  return (
    <div style={{ marginTop: 6, paddingBottom: 'calc(84px + env(safe-area-inset-bottom, 16px))' }}>

      {/* ── Dica do Dia ── */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(124,58,237,0.04) 100%)',
        borderRadius: 12, padding: '12px 14px', marginBottom: 10,
        border: '1px solid rgba(37,99,235,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: 'rgba(37,99,235,0.1)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <DicaIcon size={15} style={{ color: '#2563eb' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Dica do dia
              </span>
              <Sparkles size={10} style={{ color: '#2563eb', opacity: 0.5 }} />
            </div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 2 }}>
              {dica.title}
            </div>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-muted, #6b7280)', lineHeight: 1.45 }}>
              {dica.text}
            </div>
          </div>
        </div>
      </div>

      {/* ── Pilares do Programa ── */}
      <div style={{
        background: 'var(--bg-card, white)', borderRadius: 12, overflow: 'hidden',
        marginBottom: 10, border: '1px solid var(--border-color, #e5e7eb)',
      }}>
        <div style={{ padding: '9px 14px', borderBottom: '1px solid var(--border-color, #f3f4f6)' }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Programa Escola Segura e Inteligente
          </div>
          <div style={{ fontSize: '0.52rem', color: 'var(--text-muted, #9ca3af)', marginTop: 1 }}>
            6 pilares que transformam a educacao publica
          </div>
        </div>

        <div style={{ padding: '4px 8px' }}>
          {PILARES.map((p, i) => {
            const Icon = p.icon;
            const isOpen = pilarExpandido === i;
            return (
              <div key={i} style={{ marginBottom: isOpen ? 8 : 0, transition: 'margin 0.2s' }}>
                <div
                  onClick={() => setPilarExpandido(isOpen ? null : i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px',
                    cursor: 'pointer',
                    borderBottom: !isOpen && i < PILARES.length - 1 ? '1px solid var(--border-color, #f3f4f6)' : 'none',
                  }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: isOpen ? p.gradient : p.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.25s ease',
                    boxShadow: isOpen ? `0 3px 10px ${p.color}30` : 'none',
                  }}>
                    <Icon size={14} style={{ color: isOpen ? '#fff' : p.color, transition: 'color 0.2s' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isOpen ? p.color : 'var(--text-main)' }}>
                      {p.label}
                    </span>
                  </div>
                  <ChevronRight size={13} style={{
                    color: isOpen ? p.color : 'var(--text-muted, #9ca3af)',
                    transition: 'transform 0.25s ease, color 0.2s',
                    transform: isOpen ? 'rotate(90deg)' : 'none',
                  }} />
                </div>

                {/* ── Expanded Rich Content ── */}
                {isOpen && (
                  <div style={{
                    background: `${p.bg}`,
                    borderRadius: 10, padding: '10px 12px', marginLeft: 4, marginRight: 4,
                    borderLeft: `3px solid ${p.color}`,
                    animation: 'slideDown 0.25s ease',
                  }}>
                    {/* Description */}
                    <div style={{
                      fontSize: '0.6rem', color: 'var(--text-muted, #4b5563)',
                      lineHeight: 1.55, marginBottom: 10,
                    }}>
                      {p.desc}
                    </div>

                    {/* Feature bullets */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {p.features.map((f, fi) => {
                        const FIcon = f.icon;
                        return (
                          <div key={fi} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '5px 8px', borderRadius: 7,
                            background: 'var(--bg-card, white)',
                            border: '1px solid var(--border-color, #f3f4f6)',
                          }}>
                            <div style={{
                              width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                              background: p.gradient, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              <FIcon size={10} style={{ color: '#fff' }} />
                            </div>
                            <span style={{ fontSize: '0.58rem', fontWeight: 600, color: 'var(--text-main)', flex: 1 }}>
                              {f.text}
                            </span>
                            <CheckCircle2 size={10} style={{ color: p.color, opacity: 0.5, flexShrink: 0 }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Frase Motivacional ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 50%, #7c3aed 100%)',
        borderRadius: 12, padding: '14px 16px', marginBottom: 10,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -15, right: -15, width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '0.5rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
            Inspiracao do dia
          </div>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.92)', lineHeight: 1.5, fontStyle: 'italic' }}>
            "{frase.texto}"
          </div>
          <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginTop: 6, textAlign: 'right' }}>
            — {frase.autor}
          </div>
        </div>
      </div>

      {/* ── App version ── */}
      <div style={{ textAlign: 'center', padding: '6px 0 12px', color: 'var(--text-muted, #9ca3af)' }}>
        <div style={{ fontSize: '0.5rem', fontWeight: 600, opacity: 0.4 }}>
          Aluno Conectado v2026 · Programa Escola Segura e Inteligente
        </div>
      </div>

    </div>
  );
}
